import { useState } from 'react';

import { Account } from '@ndvr/portfolio-api';

import { EditableCard } from '@/components/EditableCard';
import { useEditableCardGroup } from '@/components/EditableCardGroupContext';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { formatCurrency, formatPercent, fractionalToPercentString, percentToFractionalString } from 'utils';
import { useAccountYtdTaxMetrics } from 'views/account/tax-metrics-queries';
import { useUpdateTaxBudget } from 'views/portfolio/account-queries';

import { TaxBudgetUsage } from './TaxBudgetUsage';

const RateRow = ({
    label,
    editing,
    editableValue,
    onChange,
    error,
    effective,
    override,
}: {
    label: string;
    editing: boolean;
    editableValue: string;
    onChange: (value: string) => void;
    error: string | null;
    effective: string;
    override: string | null | undefined;
}) => {
    const asPercent = formatPercent(Number(effective) * 100);
    return (
        <div className="space-y-2">
            <div className="text-muted-foreground text-sm font-medium">{label}</div>
            <div className="font-medium">
                {editing ? (
                    <>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="99.99"
                            value={editableValue}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="Leave blank for default"
                            className="w-full"
                        />
                        {error && <p className="text-destructive text-sm font-medium">{error}</p>}
                    </>
                ) : override == null ? (
                    <>
                        {asPercent}
                        <span className="text-muted-foreground ml-1 font-normal">(default)</span>
                    </>
                ) : (
                    asPercent
                )}
            </div>
        </div>
    );
};

export const TaxBudgetCard = ({ account, editGroup }: { account: Account; editGroup?: string }) => {
    const [editableTaxBudget, setEditableTaxBudget] = useState<string | null>('');
    const [editableShortTermRate, setEditableShortTermRate] = useState<string>('');
    const [editableLongTermRate, setEditableLongTermRate] = useState<string>('');

    const { mutate: updateTaxBudget, isPending } = useUpdateTaxBudget(account.accountGUID, account.accountGroupGUID);
    const { data: metrics, isLoading: isLoadingMetrics } = useAccountYtdTaxMetrics(account.accountGUID);
    const editGroupContext = useEditableCardGroup();
    const isEditing = editGroup ? editGroupContext.isEditing(editGroup, 'tax-budget') : false;

    const handleEdit = () => {
        setEditableTaxBudget(
            account.taxBudget == null && account.taxBudgetYear != null
                ? null // unlimited
                : (account.taxBudget ?? ''), // specific amount or empty if not set
        );
        setEditableShortTermRate(fractionalToPercentString(account.shortTermCapGainsTaxRateOverride, ''));
        setEditableLongTermRate(fractionalToPercentString(account.longTermCapGainsTaxRateOverride, ''));
    };

    const handleCancel = () => {
        setEditableTaxBudget('');
        setEditableShortTermRate('');
        setEditableLongTermRate('');
    };

    const handleSave = () => {
        const unlimited = editableTaxBudget === null;
        const hasAmount = !unlimited && editableTaxBudget !== '';
        updateTaxBudget(
            {
                taxBudget: (unlimited ? null : hasAmount ? editableTaxBudget : undefined) as string | undefined,
                taxBudgetYear: unlimited || hasAmount ? new Date().getFullYear() : undefined,
                shortTermCapGainsTaxRateOverride: toOverride(editableShortTermRate),
                longTermCapGainsTaxRateOverride: toOverride(editableLongTermRate),
            },
            {
                onSuccess: () => {
                    if (editGroup) editGroupContext.setEditingCard(editGroup, null);
                },
            },
        );
    };

    const handleUnlimitedToggle = (checked: boolean) => {
        setEditableTaxBudget(checked ? null : '');
    };

    const getTaxBudgetError = (value: string | null): string | null => {
        if (value === null || value === '') return null;
        const numValue = Number(value);
        if (isNaN(numValue)) return 'Tax budget must be a valid number';
        if (numValue < 0) return 'Tax budget must not be negative';
        return null;
    };

    const getRateError = (value: string, label: string): string | null => {
        if (value === '') return null;
        const numValue = Number(value);
        if (isNaN(numValue)) return `${label} must be a number`;
        if (numValue < 0) return `${label} can't be negative`;
        if (numValue >= 99.995) return `${label} must be less than 100%`;
        return null;
    };

    const toOverride = (value: string): string | undefined => {
        if (value === '') return undefined;
        const fractional = percentToFractionalString(value);
        if (fractional === undefined) {
            console.error(`Invalid cap gains rate reached save path: ${value}`);
            return undefined;
        }
        return fractional;
    };

    const taxBudgetError = getTaxBudgetError(editableTaxBudget);
    const shortTermRateError = getRateError(editableShortTermRate, 'Short-term rate');
    const longTermRateError = getRateError(editableLongTermRate, 'Long-term rate');

    const accountIsUnlimited = account.taxBudget == null && account.taxBudgetYear != null;
    const hasTaxBudgetChanged =
        editableTaxBudget === null ? !accountIsUnlimited : editableTaxBudget !== (account.taxBudget ?? '');
    const hasShortTermRateChanged =
        editableShortTermRate !== fractionalToPercentString(account.shortTermCapGainsTaxRateOverride, '');
    const hasLongTermRateChanged =
        editableLongTermRate !== fractionalToPercentString(account.longTermCapGainsTaxRateOverride, '');
    const hasChanges = hasTaxBudgetChanged || hasShortTermRateChanged || hasLongTermRateChanged;

    const isMissingAmount = editableTaxBudget !== null && editableTaxBudget === '';
    const hasErrors = taxBudgetError !== null || shortTermRateError !== null || longTermRateError !== null;
    const saveDisabled = !hasChanges || hasErrors || isMissingAmount;

    const currentYear = new Date().getFullYear();
    const isStaleYear = account.taxBudgetYear != null && account.taxBudgetYear !== currentYear;

    const displayValue = () => {
        if (isStaleYear) {
            const staleAmount = account.taxBudget == null ? 'Unlimited' : formatCurrency(account.taxBudget) || '$0';
            return (
                <>
                    {formatCurrency('0')}
                    <span className="text-muted-foreground ml-1 font-normal">
                        ({staleAmount} in {account.taxBudgetYear})
                    </span>
                </>
            );
        }
        if (account.taxBudget == null && account.taxBudgetYear != null) return 'Unlimited';
        if (account.taxBudget == null) return formatCurrency('0'); // taxBudgetYear == null too → not set
        return formatCurrency(account.taxBudget);
    };

    return (
        <EditableCard
            headerClassName="pt-3 pb-3"
            title="Tax Budget"
            editGroup={editGroup}
            editCardId="tax-budget"
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            saveDisabled={saveDisabled}
            saving={isPending}
            saveTooltipMessage={
                isMissingAmount
                    ? 'Enter a budget amount or set to unlimited'
                    : hasErrors
                      ? 'Fix validation errors before saving'
                      : saveDisabled
                        ? 'No changes to save'
                        : ''
            }
            hasChanges={hasChanges}
        >
            <div className="space-y-4">
                {!isEditing && (
                    <>
                        <TaxBudgetUsage account={account} metrics={metrics ?? undefined} isLoading={isLoadingMetrics} />
                        <hr />
                    </>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <div className="text-muted-foreground text-sm font-medium">Tax Budget</div>
                        <div className="font-medium">
                            {isEditing ? (
                                <>
                                    <Input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={editableTaxBudget ?? ''}
                                        onChange={(e) => setEditableTaxBudget(e.target.value)}
                                        placeholder="Enter tax budget"
                                        className="w-full"
                                        disabled={editableTaxBudget === null}
                                    />
                                    {taxBudgetError && (
                                        <p className="text-destructive text-sm font-medium">{taxBudgetError}</p>
                                    )}
                                </>
                            ) : (
                                displayValue()
                            )}
                        </div>
                    </div>

                    {isEditing && (
                        <div className="space-y-2">
                            <div className="text-muted-foreground text-sm font-medium">Unlimited</div>
                            <div className="flex h-10 items-center space-x-2">
                                <Switch checked={editableTaxBudget === null} onCheckedChange={handleUnlimitedToggle} />
                                <span className="font-medium">{editableTaxBudget === null ? 'Yes' : 'No'}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <RateRow
                        label="Short-Term Cap Gains Tax Rate"
                        editing={isEditing}
                        editableValue={editableShortTermRate}
                        onChange={setEditableShortTermRate}
                        error={shortTermRateError}
                        effective={account.shortTermCapGainsTaxRate}
                        override={account.shortTermCapGainsTaxRateOverride}
                    />
                    <RateRow
                        label="Long-Term Cap Gains Tax Rate"
                        editing={isEditing}
                        editableValue={editableLongTermRate}
                        onChange={setEditableLongTermRate}
                        error={longTermRateError}
                        effective={account.longTermCapGainsTaxRate}
                        override={account.longTermCapGainsTaxRateOverride}
                    />
                </div>

                {!isEditing && isStaleYear && (
                    <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                        Tax budget is set for {account.taxBudgetYear}, not the current year. Effective tax budget for{' '}
                        {currentYear} is $0.
                    </div>
                )}
                {!isEditing && account.taxBudget == null && account.taxBudgetYear == null && (
                    <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                        No tax budget has been set. Effective tax budget for {currentYear} is $0.
                    </div>
                )}
            </div>
        </EditableCard>
    );
};
